import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API = `${window.location.origin}/api`;

export default function TrialModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: ""
  });
  const [loading, setLoading] = useState(false);

  const trialFeatures = [
    "Full access to all features",
    "30-day free trial",
    "No credit card required",
    "Dedicated onboarding support"
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Using demo-request endpoint for trial signups as well
      await axios.post(`${API}/demo-request`, {
        ...formData,
        message: "Free Trial Request"
      });
      toast.success("Trial request submitted! Check your email for setup instructions.");
      setFormData({ name: "", email: "", company: "", phone: "" });
      onClose();
    } catch (error) {
      toast.error("Failed to submit request. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] bg-white border-slate-200 shadow-2xl" data-testid="trial-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900">Start Your Free Trial</DialogTitle>
          <p className="text-slate-600 mt-2">
            Get started with KineServe in minutes. No credit card required.
          </p>
        </DialogHeader>

        {/* Trial Benefits */}
        <div className="bg-slate-50 p-4 my-4 rounded-lg">
          <div className="grid grid-cols-2 gap-3">
            {trialFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-2">
                <CheckCircle2 size={16} className="text-green-600 flex-shrink-0" />
                <span className="text-sm text-slate-700">{feature}</span>
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="form-label">Name *</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="John Doe"
                required
                className="bg-white"
                data-testid="trial-name-input"
              />
            </div>
            <div>
              <label className="form-label">Work Email *</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="john@company.com"
                required
                className="bg-white"
                data-testid="trial-email-input"
              />
            </div>
          </div>

          <div>
            <label className="form-label">Company *</label>
            <Input
              value={formData.company}
              onChange={(e) => setFormData({...formData, company: e.target.value})}
              placeholder="Your Company Name"
              required
              className="bg-white"
              data-testid="trial-company-input"
            />
          </div>

          <div>
            <label className="form-label">Phone (Optional)</label>
            <Input
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              placeholder="+91 123 456 7890"
              className="bg-white"
              data-testid="trial-phone-input"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 text-white hover:bg-blue-700 py-4 font-medium btn-lift"
            data-testid="trial-submit-btn"
          >
            {loading ? "Creating Account..." : "Start Free Trial"}
          </Button>

          <p className="text-xs text-slate-500 text-center">
            By signing up, you agree to our Privacy Policy and Terms of Service.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
